/**
 * FERP Portal - Document Studio Page
 *
 * Complete Chaos-to-DSL workflow in a single, unified interface.
 * Composes existing chaos components from @ferp/workflow-forms into a
 * tabbed workflow: Upload -> AI Conversion -> Review -> Approval -> Documents
 *
 * Available to ALL user types (User, Admin, Architect). Backend controls
 * which actions are permitted per role via approval policies and permissions.
 *
 * DRR-072: Complete meta-orchestration pipeline UI
 *
 * FERP Principles:
 * - Dumb Frontend: All data from backend via hooks
 * - Mantine v8.x only (no MUI)
 * - Repository pattern for data access
 * - Thin page wrapper composing existing components
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Tabs,
  Alert,
  Badge,
  Group,
  ThemeIcon,
  Table,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  SimpleGrid,
  Paper,
  UnstyledButton,
  TextInput,
  Select,
  Textarea,
  ScrollArea,
  Button,
  Progress,
} from '@mantine/core';
import {
  IconUpload,
  IconBrain,
  IconEye,
  IconRubberStamp,
  IconFileText,
  IconPackageImport,
  IconPlayerPlay,
  IconInfoCircle,
  IconCertificate,
  IconTable,
  IconMessageCircle,
  IconSend,
  IconDownload,
  IconX,
} from '@tabler/icons-react';
import {
  InputSourcePanel,
  ConversionProgressTracker,
  EvidencePreview,
  TemplateRefinementEditor,
  DocumentLocationVisualizer,
  ConfidenceScoreBreakdown,
  ApprovalWorkflowIntegration,
} from '@ferp/workflow-forms';
import type {
  TemplateForApproval,
  ConfidenceMetrics,
} from '@ferp/workflow-forms';
import {
  useConvertChaos,
  useChaosSchemaList,
} from '@ferp/workflow-forms';
import type {
  ConversionResult,
  ChaosDocument,
  StructuredImportJob,
} from '@ferp/workflow-forms';
import { chaosDocumentRepository } from '@ferp/workflow-forms';
import { IntakeTargetSelector } from '@/components/intake';

// ============================================================
// Component
// ============================================================

// DRR-137 Phase 5: Import job status → badge color (backend-sourced string values)
function importJobStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    COMPLETED: 'green',
    FAILED: 'red',
    RUNNING: 'blue',
    PENDING: 'gray',
  };
  return colorMap[status] ?? 'gray';
}

export function DocumentStudioPage() {
  const [activeTab, setActiveTab] = useState<string | null>('upload');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null);
  const [templateForApproval, setTemplateForApproval] = useState<TemplateForApproval | null>(null);
  const [intakeTarget, setIntakeTarget] = useState<string>('DOCUMENT');
  // DRR-137 Phase 3: Evidence attachment state
  const [evidenceEntityType, setEvidenceEntityType] = useState<string>('TASK');
  const [evidenceEntityId, setEvidenceEntityId] = useState('');
  const [attachedEvidence, setAttachedEvidence] = useState<{ documentId: string; entityId: string } | null>(null);
  // DRR-137 Phase 4: Intake conversation state
  const [conversationMessages, setConversationMessages] = useState<Array<{ role: string; content: string; messageId: string; createdAt: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  // DRR-137 Phase 5: Structured import state
  const [importCsvContent, setImportCsvContent] = useState('');
  const [importJob, setImportJob] = useState<StructuredImportJob | null>(null);
  const [isRunningImport, setIsRunningImport] = useState(false);

  const { documents, listDocuments, isLoading: docsLoading } = useChaosSchemaList();
  const { convertToDSL, conversionStatus, isConverting } = useConvertChaos();

  // Load documents on mount
  useEffect(() => {
    listDocuments();
  }, [listDocuments]);

  // Handle upload completion — advance to conversion tab (or stay in upload for evidence attachment)
  const handleUploadComplete = useCallback((results: Array<{ document: { documentId: string } }>) => {
    const docId = results[0]?.document?.documentId;
    if (!docId) return;

    setSelectedDocumentId(docId);
    listDocuments();

    if (intakeTarget === 'EVIDENCE_PACKAGE' && evidenceEntityId.trim()) {
      // Stay on upload tab — show attachment confirmation
      setAttachedEvidence({ documentId: docId, entityId: evidenceEntityId });
    } else {
      setActiveTab('conversion');
    }
  }, [listDocuments, intakeTarget, evidenceEntityId]);

  // Handle conversion completion — advance to review tab
  const handleConversionComplete = useCallback((confidence: number) => {
    // Conversion result is set via the hook's state
    setActiveTab('review');
  }, []);

  // DRR-137 Phase 4: Send intake conversation message
  const handleSendMessage = useCallback(async () => {
    if (!chatInput.trim() || !selectedDocumentId || isSendingMessage) return;
    const userContent = chatInput.trim();
    setChatInput('');
    setIsSendingMessage(true);

    // Optimistically add user message
    const optimisticUser = { role: 'USER', content: userContent, messageId: `opt-${Date.now()}`, createdAt: new Date().toISOString() };
    setConversationMessages((prev) => [...prev, optimisticUser]);

    try {
      // Tenant comes from the first document in the list or a fallback
      const tenant = (documents[0] as unknown as Record<string, unknown>)?.tenant as string ?? 'default';
      // NOTE: chaosDocumentRepository is imported from @ferp/workflow-forms
      // For now we use the useMutation pattern — this will be wired via the hook layer
      // when the generated schema is updated. Placeholder response until SDL sync.
      const assistantMsg = { role: 'ASSISTANT', content: '(AI response pending SDL sync)', messageId: `ast-${Date.now()}`, createdAt: new Date().toISOString() };
      setConversationMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      console.error('Failed to send intake message:', err);
    } finally {
      setIsSendingMessage(false);
    }
  }, [chatInput, selectedDocumentId, isSendingMessage, documents]);

  // DRR-137 Phase 5: Run structured CSV import
  const handleRunImport = useCallback(async () => {
    if (!importCsvContent.trim() || !selectedDocumentId || isRunningImport) return;
    setIsRunningImport(true);
    setImportJob(null);
    try {
      const tenant = (documents[0] as unknown as Record<string, unknown>)?.tenant as string ?? 'default';
      const job = await chaosDocumentRepository.initiateStructuredImport({
        documentId: selectedDocumentId,
        tenant,
        initiatedBy: 'portal-user', // resolved from auth in production via AuthContext
        primitiveType: 'ACTOR',
        csvContent: importCsvContent,
      });
      setImportJob(job);
    } catch (err) {
      console.error('Structured import failed:', err);
    } finally {
      setIsRunningImport(false);
    }
  }, [importCsvContent, selectedDocumentId, isRunningImport, documents]);

  // DRR-137 Phase 5: Download Actor CSV template
  const handleDownloadTemplate = useCallback(async () => {
    try {
      const csv = await chaosDocumentRepository.getActorImportCsvTemplate();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'actor-import-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download template:', err);
    }
  }, []);

  // Start conversion for selected document
  const handleStartConversion = useCallback(async () => {
    if (!selectedDocumentId) return;
    const result = await convertToDSL(selectedDocumentId);
    if (result) {
      setConversionResult(result);
      setActiveTab('review');
    }
  }, [selectedDocumentId, convertToDSL]);

  // Handle refinement submission — build TemplateForApproval and advance
  const handleRefinementSubmit = useCallback((result: {
    templateId: string;
    updatedConfidence: { overall: number };
  }) => {
    if (!conversionResult) return;

    const template = conversionResult.template;
    const approval: TemplateForApproval = {
      template_id: result.templateId,
      template_version: template.template_version ?? '1.0.0-draft',
      tenant: (template as unknown as Record<string, unknown>).tenant as string ?? '',
      business: {
        // @ts-expect-error Property access
        name: template.name ?? 'Untitled Template',
        // @ts-expect-error Property access
        domain: template.domain ?? 'general',
        // @ts-expect-error Property access
        category: template.category ?? 'workflow',
      },
      compliance: {
        // @ts-expect-error Property access
        frameworks: template.compliance_frameworks ?? [],
      },
      confidence_score: result.updatedConfidence.overall,
    };

    setTemplateForApproval(approval);
    setActiveTab('approval');
  }, [conversionResult]);

  // Handle clicking a document in the Documents tab to start conversion
  const handleDocumentSelect = useCallback((docId: string) => {
    setSelectedDocumentId(docId);
    setConversionResult(null);
    setTemplateForApproval(null);
    setActiveTab('conversion');
  }, []);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Page Header */}
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="violet">
            <IconPackageImport size={20} />
          </ThemeIcon>
          <div>
            <Title order={2}>Intake Studio</Title>
            <Text size="sm" c="dimmed">
              Ingest external data into FERP — documents, rule sets, evidence, structured imports
            </Text>
          </div>
        </Group>

        {/* Tabbed Workflow */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="upload" leftSection={<IconUpload size={16} />}>
              Upload
            </Tabs.Tab>
            <Tabs.Tab
              value="conversion"
              leftSection={<IconBrain size={16} />}
            >
              AI Conversion
            </Tabs.Tab>
            <Tabs.Tab
              value="review"
              leftSection={<IconEye size={16} />}
            >
              Review
            </Tabs.Tab>
            <Tabs.Tab
              value="approval"
              leftSection={<IconRubberStamp size={16} />}
            >
              Approval
            </Tabs.Tab>
            <Tabs.Tab value="documents" leftSection={<IconFileText size={16} />}>
              Documents
              {documents.length > 0 && (
                <Badge size="xs" variant="filled" color="gray" ml={6}>
                  {documents.length}
                </Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab
              value="conversation"
              leftSection={<IconMessageCircle size={16} />}
              disabled={!selectedDocumentId}
            >
              Conversation
              {conversationMessages.length > 0 && (
                <Badge size="xs" variant="filled" color="violet" ml={6}>
                  {conversationMessages.length}
                </Badge>
              )}
            </Tabs.Tab>
          </Tabs.List>

          {/* ── Tab 1: Upload / Capture ── */}
          <Tabs.Panel value="upload" pt="md">
            <Stack gap="md">
              {/* DRR-220: Hybrid intake target selector with categories */}
              <div>
                <Text size="sm" fw={500} mb="xs">What are you ingesting?</Text>
                <IntakeTargetSelector
                  value={intakeTarget}
                  onChange={setIntakeTarget}
                  showCategories={true}
                />
              </div>

              {/* DRR-137 Phase 3: Entity linker — shown when EVIDENCE_PACKAGE selected */}
              {intakeTarget === 'EVIDENCE_PACKAGE' && (
                <Paper withBorder p="md" radius="md">
                  <Stack gap="xs">
                    <Group gap="xs">
                      <ThemeIcon size="sm" variant="light" color="orange">
                        <IconCertificate size={14} />
                      </ThemeIcon>
                      <Text size="sm" fw={600}>Link evidence to an existing entity</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      After upload, a typed EVIDENCED_BY edge (DRR-110) will be created between
                      the document and the entity you specify.
                    </Text>
                    <Group grow>
                      <Select
                        label="Entity type"
                        size="xs"
                        value={evidenceEntityType}
                        onChange={(v) => setEvidenceEntityType(v ?? 'TASK')}
                        data={[
                          { value: 'TASK', label: 'Task' },
                          { value: 'INSTANCE', label: 'Workflow Instance' },
                          { value: 'WORKFLOW', label: 'Workflow' },
                          { value: 'ISSUE', label: 'Issue' },
                        ]}
                      />
                      <TextInput
                        label="Entity ID"
                        placeholder="e.g. tsk_L3SPe4ijsO"
                        size="xs"
                        value={evidenceEntityId}
                        onChange={(e) => setEvidenceEntityId(e.currentTarget.value)}
                      />
                    </Group>
                    {attachedEvidence && (
                      <Alert color="green" variant="light" icon={<IconInfoCircle size={14} />}>
                        Evidence attached: document <strong>{attachedEvidence.documentId}</strong> →
                        entity <strong>{attachedEvidence.entityId}</strong>
                      </Alert>
                    )}
                  </Stack>
                </Paper>
              )}

              {/* DRR-137 Phase 5: Structured import panel — shown when STRUCTURED_IMPORT selected */}
              {intakeTarget === 'STRUCTURED_IMPORT' && (
                <Paper withBorder p="md" radius="md">
                  <Stack gap="sm">
                    <Group gap="xs" justify="space-between">
                      <Group gap="xs">
                        <ThemeIcon size="sm" variant="light" color="green">
                          <IconTable size={14} />
                        </ThemeIcon>
                        <Text size="sm" fw={600}>Structured Actor Import</Text>
                      </Group>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="gray"
                        leftSection={<IconDownload size={12} />}
                        onClick={handleDownloadTemplate}
                      >
                        Download template
                      </Button>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Paste CSV content below (headers: name, actor_type, role, description).
                      Max 100 rows. Duplicate rows are skipped automatically.
                    </Text>
                    <Textarea
                      placeholder={`name,actor_type,role,description\nJane Smith,HUMAN,cna,Certified Nursing Assistant\nIntake Bot,AI,,AI-powered intake assistant`}
                      minRows={6}
                      maxRows={12}
                      value={importCsvContent}
                      onChange={(e) => setImportCsvContent(e.currentTarget.value)}
                      styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
                    />
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed">
                        {importCsvContent.split('\n').filter(Boolean).length > 0
                          ? `${Math.max(0, importCsvContent.split('\n').filter(Boolean).length - 1)} data rows`
                          : 'No content yet'}
                      </Text>
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconPlayerPlay size={12} />}
                        loading={isRunningImport}
                        disabled={!importCsvContent.trim() || !selectedDocumentId}
                        onClick={handleRunImport}
                      >
                        Run Import
                      </Button>
                    </Group>

                    {/* Import job results */}
                    {importJob && (
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Badge
                            color={importJobStatusColor(importJob.status)}
                            variant="light"
                            size="sm"
                          >
                            {importJob.status}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {importJob.createdCount} created · {importJob.errorCount} errors · {importJob.totalRows} total
                          </Text>
                        </Group>
                        {importJob.totalRows > 0 && (
                          <Progress
                            value={(importJob.createdCount / importJob.totalRows) * 100}
                            color="green"
                            size="xs"
                          />
                        )}
                        {importJob.errorMessage && (
                          <Alert color="red" variant="light" icon={<IconX size={14} />}>
                            {importJob.errorMessage}
                          </Alert>
                        )}
                        {importJob.results.length > 0 && (
                          <Table striped withTableBorder fz="xs">
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th w={40}>#</Table.Th>
                                <Table.Th>Name</Table.Th>
                                <Table.Th w={80}>Status</Table.Th>
                                <Table.Th>Entity ID / Error</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {importJob.results.map((row) => (
                                <Table.Tr key={row.rowNum}>
                                  <Table.Td>{row.rowNum}</Table.Td>
                                  <Table.Td>{row.inputSummary}</Table.Td>
                                  <Table.Td>
                                    <Badge
                                      size="xs"
                                      color={row.status === 'created' ? 'green' : row.status === 'skipped' ? 'gray' : 'red'}
                                      variant="light"
                                    >
                                      {row.status}
                                    </Badge>
                                  </Table.Td>
                                  <Table.Td>
                                    {row.entityId ? (
                                      <Text size="xs" c="dimmed" ff="monospace">{row.entityId}</Text>
                                    ) : row.error ? (
                                      <Text size="xs" c="red">{row.error}</Text>
                                    ) : null}
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        )}
                      </Stack>
                    )}

                    {!selectedDocumentId && (
                      <Alert color="yellow" variant="light" icon={<IconInfoCircle size={14} />}>
                        Upload a document first to link the import job (for audit trail).
                      </Alert>
                    )}
                  </Stack>
                </Paper>
              )}

              <InputSourcePanel
                onUploadComplete={handleUploadComplete}
              />
            </Stack>
          </Tabs.Panel>

          {/* ── Tab 2: AI Conversion ── */}
          <Tabs.Panel value="conversion" pt="md">
            {selectedDocumentId ? (
              <Stack gap="md">
                <ConversionProgressTracker
                  documentId={selectedDocumentId}
                  onComplete={handleConversionComplete}
                />
                {conversionResult?.confidence && (
                  <ConfidenceScoreBreakdown
                    confidence={conversionResult.confidence}
                  />
                )}
                {!isConverting && !conversionResult && (
                  <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="blue"
                    variant="light"
                  >
                    Document ready. The 11-phase AI conversion pipeline will
                    analyze your document and generate a validated workflow template.
                  </Alert>
                )}
              </Stack>
            ) : (
              <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                Upload a document first to begin AI conversion.
              </Alert>
            )}
          </Tabs.Panel>

          {/* ── Tab 3: Review ── */}
          <Tabs.Panel value="review" pt="md">
            {conversionResult ? (
              <Stack gap="md">
                <EvidencePreview
                  conversionResult={conversionResult}
                  onProceedToRefinement={() => {
                    // Scroll handled by component internally
                  }}
                />
                {conversionResult.evidence && (
                  <DocumentLocationVisualizer
                    fieldEvidence={conversionResult.evidence}
                  />
                )}
                <TemplateRefinementEditor
                  templateId={conversionResult.template?.template_id ?? conversionResult.extraction_id}
                  onSubmit={handleRefinementSubmit}
                />
              </Stack>
            ) : (
              <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                Complete AI conversion first to review the generated template.
              </Alert>
            )}
          </Tabs.Panel>

          {/* ── Tab 4: Approval ── */}
          <Tabs.Panel value="approval" pt="md">
            {templateForApproval ? (
              <ApprovalWorkflowIntegration
                template={templateForApproval}
                onSubmitSuccess={(instanceId) => {
                  // Refresh document list after approval submission
                  listDocuments();
                }}
              />
            ) : (
              <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                Review and refine a template first, then submit for approval.
              </Alert>
            )}
          </Tabs.Panel>

          {/* ── Tab 5: Documents ── */}
          <Tabs.Panel value="documents" pt="md">
            {docsLoading ? (
              <Center py="xl">
                <Loader size="md" type="dots" />
              </Center>
            ) : documents.length === 0 ? (
              <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
                No documents uploaded yet. Use the Upload tab to get started.
              </Alert>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Filename</Table.Th>
                    <Table.Th>Domain</Table.Th>
                    <Table.Th>Complexity</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Conversions</Table.Th>
                    <Table.Th>Uploaded</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {documents.map((doc: ChaosDocument) => (
                    <Table.Tr key={doc.document_id}>
                      <Table.Td>
                        <Text size="sm" fw={500}>{doc.filename}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light">{doc.detected_domain}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          color={
                            doc.detected_complexity === 'CRITICAL' ? 'red'
                              : doc.detected_complexity === 'HIGH' ? 'orange'
                              : doc.detected_complexity === 'MEDIUM' ? 'yellow'
                              : 'green'
                          }
                        >
                          {doc.detected_complexity}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="outline">{doc.upload_status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{doc.conversion_count}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="Convert to template">
                          <ActionIcon
                            variant="light"
                            color="violet"
                            onClick={() => handleDocumentSelect(doc.document_id)}
                          >
                            <IconPlayerPlay size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Tabs.Panel>

          {/* ── Tab 6: Conversation (DRR-137 Phase 4) ── */}
          <Tabs.Panel value="conversation" pt="md">
            {selectedDocumentId ? (
              <Stack gap="sm" style={{ height: '60vh' }}>
                {/* Message history */}
                <ScrollArea flex={1} style={{ flex: 1 }}>
                  <Stack gap="xs" p="xs">
                    {conversationMessages.length === 0 ? (
                      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                        Ask the AI assistant about this document — clarify intent, resolve
                        ambiguity, or get guidance on which intake target to choose.
                      </Alert>
                    ) : (
                      conversationMessages.map((msg) => (
                        <Paper
                          key={msg.messageId}
                          p="sm"
                          radius="md"
                          withBorder
                          style={{
                            alignSelf: msg.role === 'USER' ? 'flex-end' : 'flex-start',
                            maxWidth: '80%',
                            backgroundColor: msg.role === 'USER'
                              ? 'var(--mantine-color-violet-0)'
                              : undefined,
                          }}
                        >
                          <Text size="xs" c="dimmed" mb={4}>
                            {msg.role === 'USER' ? 'You' : 'Intake AI'} ·{' '}
                            {new Date(msg.createdAt).toLocaleTimeString()}
                          </Text>
                          <Text size="sm">{msg.content}</Text>
                        </Paper>
                      ))
                    )}
                  </Stack>
                </ScrollArea>

                {/* Input area */}
                <Group gap="xs" align="flex-end">
                  <Textarea
                    style={{ flex: 1 }}
                    placeholder="Ask about this document…"
                    autosize
                    minRows={1}
                    maxRows={4}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <ActionIcon
                    size="lg"
                    variant="filled"
                    color="violet"
                    onClick={handleSendMessage}
                    loading={isSendingMessage}
                    disabled={!chatInput.trim()}
                  >
                    <IconSend size={16} />
                  </ActionIcon>
                </Group>
              </Stack>
            ) : (
              <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                Upload a document first to start an intake conversation.
              </Alert>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
